/**
 * NearbyClassesFallback - Recovery block for the public /events empty state.
 *
 * When the primary radius search returns 0 results, EventSearchPage runs a
 * wider fallback search and passes the nearest stops here so the studio isn't
 * left at a dead end: a banner pitches a hosted custom masterclass (CTA in the
 * first viewport), followed by the nearest stops as a travel alternative.
 */

import { Link } from 'react-router-dom';
import type { TourStop } from '../../api/events';
import { formatDateRange } from '../../api/events';

interface NearbyClassesFallbackProps {
  /** Candidate stops, expected sorted by distance ascending. Renders at most 3. */
  stops: TourStop[];
  /** The user's selected radius, surfaced in the empty-state copy. */
  searchRadius: number;
  /** Location query string (no leading '?') to preserve on outgoing links. */
  locationQuery: string;
}

export function NearbyClassesFallback({ stops, searchRadius, locationQuery }: NearbyClassesFallbackProps) {
  if (stops.length === 0) return null;

  const nearest = stops.slice(0, 3);

  // "3 within 60 miles" — furthest shown stop, rounded up to the next 10 miles.
  const distances = nearest
    .map((stop) => stop.distance_miles)
    .filter((d): d is number => d != null);
  const withinMiles = distances.length > 0 ? Math.ceil(Math.max(...distances) / 10) * 10 : null;

  return (
    <div className="space-y-6">
      {/* Hosted-class banner: pitch left, CTA right — above the fold, no scrolling */}
      <div className="bg-primary-50 rounded-xl border border-primary-200 p-6 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-bold text-warm-900 mb-1.5">
            No classes within {searchRadius} miles of your studio
          </h3>
          <p className="text-warm-600">
            We can bring one to you instead — request a custom masterclass and
            we'll host it at a theatre near your studio.
          </p>
        </div>
        <div className="flex flex-col items-stretch sm:items-center gap-1.5 flex-shrink-0">
          <Link
            to={`/inquiry/${nearest[0]!.id}?${locationQuery}&type=hosted`}
            className="btn-primary px-6 text-center whitespace-nowrap"
          >
            Request a Custom Masterclass
          </Link>
          <p className="text-sm text-warm-500 text-center">Free to ask</p>
        </div>
      </div>

      {/* Travel alternative: nearest stops from the wider search */}
      <div>
        <div className="flex flex-wrap items-baseline gap-x-2 mb-3">
          <h4 className="font-bold text-warm-900">Or travel to the nearest classes</h4>
          {withinMiles != null && (
            <span className="text-sm text-warm-500">
              {nearest.length} within {withinMiles} miles
            </span>
          )}
        </div>

        <div className="space-y-3">
          {nearest.map((stop) => (
            <Link
              key={stop.id}
              to={`/events/${stop.id}?${locationQuery}`}
              className="flex items-center justify-between gap-4 p-4 rounded-xl border border-warm-200 bg-white hover:border-primary-300 hover:bg-warm-50 transition-all group"
            >
              <div className="min-w-0">
                <p className="font-bold text-warm-900 group-hover:text-primary-600 transition-colors truncate">
                  {stop.show.name}
                </p>
                <p className="text-sm text-warm-600 truncate">
                  {stop.theater.name} — {stop.city.name}, {stop.city.state}
                </p>
                <p className="text-sm text-warm-400 mt-0.5">
                  {formatDateRange(stop.start_date, stop.end_date)}
                </p>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                {stop.distance_miles != null && (
                  <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-success-100 text-success-700">
                    {Math.round(stop.distance_miles)} mi
                  </span>
                )}
                <span className="text-sm font-semibold text-primary-600 group-hover:text-primary-700">
                  Details
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default NearbyClassesFallback;
